import React, { useState } from 'react';
import { Row, Divider, Col, Form, Input, Select, Checkbox, Button, message, Modal } from 'antd';
import getConfig from 'next/config';
import styled from 'styled-components';
import parse from 'html-react-parser';
import { SubTitle } from 'components/PageTitle';
import Avatar from 'components/Avatar';
import Newsletter from 'containers/Newsletter';
import { SkyscraperMobile, Wideboard } from 'components/Ads';
import RodoText from 'components/RodoText';
import Section from 'components/Section';
import LogoHeadingSection from 'components/LogoHeadingSection';

const { Option } = Select;

const PersonName = styled.h2`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: right;
    a {
        color: ${({ theme }) => theme.colors.greyishBrown};
    }
    margin: 0;
`;

export const Text = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    word-wrap: break-word;
    -webkit-line-clamp: 5; /* number of lines to show */
    -webkit-box-orient: vertical;
    @media (max-width: 992px) {
        display: none;
    }
    a {
        :hover {
        }
        color: ${(props) => props.color || props.theme.colors.greyishBrownTwo};
    }
`;

const PersonContainer = styled.div`
    text-align: right;
    word-wrap: break-word;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const PersonRow = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    margin-bottom: 0;
    a {
        color: ${({ theme }) => theme.colors.primary};
    }
    margin: 0;
`;

const ContactTitle = styled.p`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: right;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
    margin: 0;
`;

const ContactAddress = styled.div`
    text-align: right;

    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
`;

const PageTitle = styled.h1`
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    font-size: 32px;
    font-weight: bold;
    margin-bottom: 0px;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
    margin: 0;
`;

const PageSubTitle = styled.h2`
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    font-size: 20px;
    font-weight: 300;
    a {
        color: ${({ theme }) => theme.colors.brownishGrey};
    }
    @media (max-width: 992px) {
        text-align: center;
    }
    margin: 0;
`;

const NameCol = styled(Col)`
    @media (max-width: 992px) {
        margin-top: 65px;
    }
`;

const PersonItem = ({ person }: { person: User }) => (
    <Col xs={24} md={12} xl={8}>
        <PersonContainer>
            <Avatar person={person} size={90} />
            <PersonName>{`${person.firstName} ${person.lastName}`}</PersonName>
            <PersonRow>{person.jobTitle}</PersonRow>
            <PersonRow style={{ marginBottom: 20 }}>{person.department}</PersonRow>
            <PersonRow>{person.phone1 && `tel: ${person.phone1}`}</PersonRow>
            <PersonRow>{person.phone2 && `tel: ${person.phone2}`}</PersonRow>
            <PersonRow>
                <a href={`mailto:${person.email}`} target="_blank" rel="noreferrer">
                    {parse(person.email.replace('@', '&#8203;@'))}
                </a>
            </PersonRow>
        </PersonContainer>
    </Col>
);

export interface Props {
    contact: Contact;
    ads: PageAds;
}

const Contact = ({ contact, ads }: Props): React.JSX.Element => {
    const [messageisSending, setMessageIsSending] = useState(false);
    const [messageisSent, setMessageSent] = useState(false);

    const onSubmit = (values) => {
        message.loading({ content: 'Wysyłanie...', key: 'SendCompanyMessage' });
        setMessageIsSending(true);
        const { apiUrl } = getConfig().publicRuntimeConfig;
        fetch(`${apiUrl}/public/api/contact`, {
            method: 'post',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(values)
        }).then(() => {
            message.success({
                content: 'Wiadomość wysłana!',
                key: 'SendCompanyMessage',
                duration: 5
            });
            setMessageSent(true);
            setMessageIsSending(false);
        });
    };

    const showModal = () => {
        Modal.info({
            okText: 'Zamknij',
            width: '80%',
            title: 'Informacja o przetwarzaniu danych osobowych',
            content: <RodoText />
        });
    };
    const { name, url } = getConfig().publicRuntimeConfig;

    const { skyscraper, wideboardBottom } = ads;
    return (
        <>
            <LogoHeadingSection />
            <Section>
                <NameCol xs={24} md={{ span: 17, offset: 7 }}>
                    <PageTitle>{name}</PageTitle>
                    <PageSubTitle>
                        <a href={url}>{url}</a>
                    </PageSubTitle>
                </NameCol>
            </Section>
            <Divider />
            <Row gutter={16}>
                <Col xs={24} md={6}>
                    <SubTitle>Dane adresowe</SubTitle>
                </Col>
                <Col xs={24} md={18}>
                    <Row gutter={24}>
                        <Col xs={12} md={8}>
                            <ContactTitle>Redakcje i biura</ContactTitle>
                            <ContactAddress>
                                {contact.officeAddress.split(/\r\n|\n|\r/gm).map((line) => {
                                    return (
                                        <>
                                            {line}
                                            <br />
                                        </>
                                    );
                                })}
                            </ContactAddress>
                        </Col>
                        <Col xs={12} md={8}>
                            <ContactTitle>Wydawca</ContactTitle>
                            <ContactAddress>
                                {contact.publisherAddress.split(/\r\n|\n|\r/gm).map((line) => {
                                    return (
                                        <>
                                            {line}
                                            <br />
                                        </>
                                    );
                                })}
                            </ContactAddress>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="25px auto 0 auto" />
                </Col>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[1]} margin="25px auto 0 auto" />
                </Col>
            </Row>
            <Divider />
            {contact.editorialContacts.length && (
                <>
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <SubTitle>Redakcja</SubTitle>
                        </Col>
                        <Col xs={24} md={18}>
                            <Row gutter={[16, 32]}>
                                {contact.editorialContacts.map((person) => (
                                    <PersonItem key={person.name} person={person} />
                                ))}
                            </Row>
                        </Col>
                    </Row>
                    <Divider />
                </>
            )}
            {contact.advertisementContacts.length > 0 && (
                <>
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <SubTitle>DZIAŁ REKLAMY</SubTitle>
                        </Col>
                        <Col xs={24} md={18}>
                            <Row gutter={[16, 32]}>
                                {contact.advertisementContacts.map((person) => (
                                    <PersonItem key={person.name} person={person} />
                                ))}
                            </Row>
                        </Col>
                    </Row>
                    <Divider />
                </>
            )}
            {contact.financialContacts.length > 0 && (
                <>
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <SubTitle>FINANSE I ADMINISTRACJA</SubTitle>
                        </Col>
                        <Col xs={24} md={18}>
                            <Row gutter={[16, 32]}>
                                {contact.financialContacts.map((person) => (
                                    <PersonItem key={person.name} person={person} />
                                ))}
                            </Row>
                        </Col>
                    </Row>
                    <Divider />
                </>
            )}
            {contact.subscriptionContacts.length > 0 && (
                <>
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <SubTitle>PRENUMERATA I DYSTRYBUCJA</SubTitle>
                        </Col>
                        <Col xs={24} md={18}>
                            <Row gutter={[16, 32]}>
                                {contact.subscriptionContacts.map((person) => (
                                    <PersonItem key={person.name} person={person} />
                                ))}
                            </Row>
                        </Col>
                    </Row>
                    <Divider />
                </>
            )}
            <Row gutter={16}>
                <Col xs={24} md={6}>
                    <SubTitle>Formularz kontaktowy</SubTitle>
                </Col>
                <Col xs={24} md={12}>
                    <Form
                        name="register"
                        onFinish={onSubmit}
                        scrollToFirstError
                        onValuesChange={() => {
                            setMessageIsSending(false);
                            setMessageSent(false);
                        }}
                    >
                        <Row gutter={12}>
                            <Col span={24}>
                                <Form.Item
                                    name="category"
                                    rules={[{ required: true, message: 'Wybierz zagadnienie' }]}
                                >
                                    <Select placeholder="Typ kontaktu">
                                        <Option value="Reklama">Reklama</Option>
                                        <Option value="Prenumerata">Prenumerata</Option>
                                        <Option value="Redakcja">Redakcja</Option>
                                        <Option value="Inne">Inne</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="subject"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Proszę podać temat kontaktu!'
                                        }
                                    ]}
                                >
                                    <Input placeholder="Temat" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="text"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Proszę podać treść kontaktu!'
                                        }
                                    ]}
                                >
                                    <Input.TextArea placeholder="Treść" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Proszę podać imię i nazwisko!'
                                        }
                                    ]}
                                >
                                    <Input placeholder="Imię i nazwisko" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="street">
                                    <Input placeholder="Ulica" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="houseNumber">
                                    <Input placeholder="Numer" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="city">
                                    <Input placeholder="Miejscowość" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="zipCode">
                                    <Input placeholder="Kod pocztowy" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="phone">
                                    <Input placeholder="Telefon" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="email"
                                    rules={[
                                        {
                                            type: 'email',
                                            message: 'To nie jest poprawny adres e-mail!'
                                        },
                                        {
                                            required: true,
                                            message: 'Proszę podać adres e-mail!'
                                        }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="agreement1"
                                    valuePropName="checked"
                                    rules={[
                                        {
                                            validator: (_, value) =>
                                                value
                                                    ? Promise.resolve()
                                                    : Promise.reject(
                                                          new Error('Zgody są wymagane!')
                                                      )
                                        }
                                    ]}
                                >
                                    <Checkbox>
                                        Wyrażam zgodę na otrzymywanie informacji handlowej
                                        kierowanej do mnie za pomocą środków komunikacji
                                        elektronicznej w szczególności poczty elektronicznej zgodnie
                                        z przepisem art. 10 ust 2 ustawy z dnia 18 lipca 2002 roku o
                                        świadczeniu usług drogą elektroniczną (Dz. U. 144 z 2002 r.
                                        poz. 1204)*
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={messageisSending}
                                        disabled={messageisSent}
                                    >
                                        Wyślij
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                    <span>
                        RAVEN MEDIA sp. z o.o. informuje, że będzie administratorem dotyczących
                        Pani/Pana danych osobowych podanych w niniejszym formularzu i w toku
                        realizacji zamówionych w formularzu usług.
                        <Button onClick={showModal} type="link">
                            Więcej...
                        </Button>
                    </span>
                </Col>
            </Row>
            <Section gutter={[16, 32]} justify="center">
                <Col span={24}>
                    <Divider />
                </Col>
                <Col xs={24} md={20}>
                    <Newsletter />
                </Col>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            <Wideboard id={wideboardBottom[0]} />
        </>
    );
};

export default Contact;
